/* eslint-disable no-restricted-globals */
/*
 *
 * BlogPostDetail
 *
 */

import React from "react";
import { connect } from "react-redux";
import { delayedDispatch, setBreadcrumb, setLoader, setTitle, updateCrumb } from "store/actions";
import { Col, Row, Input, DatePicker, Select } from "antd";
import { Helmet } from "react-helmet";
import Dropzone from "react-dropzone";
import { Icon, Editor, ContentWrapper, SelectableTabs } from "components";
import Strings from "utils/strings";
import { API, Endpoints } from "utils/api";
import { toast, translate } from "utils/utils";
import Compressor from "compressorjs";
import moment from "moment";
import placeholder from "assets/images/placeholders/image.jpg";
import { replace } from "connected-react-router";
import "./styles.scss";
import { Action } from "components/Breadcrumb";
import TextArea from "antd/lib/input/TextArea";

export class BlogPostDetail extends React.Component<any, any> {
    constructor(props: any) {
        super(props);

        this.state = {
            post: {},
            selectedTab: "general",

            active: true,
            highlight: false,
            mainImage: null,
            url: "",
            title: {},
            images: [],
            language: "en",
            hasUnsavedFields: false,
            filesToDelete: [],
            error: {},
            authors: [],
            defaultTags: [],
            tags: [],
            date: moment().toISOString(),
        };

        this.handleSwitch = this.handleSwitch.bind(this);
        this.handleHighlightSwitch = this.handleHighlightSwitch.bind(this);
        this.handleSave = this.handleSave.bind(this);
        this.handleLanguageChange = this.handleLanguageChange.bind(this);
        this.onAuthorChange = this.onAuthorChange.bind(this);
        this.handleSendNewsletter = this.handleSendNewsletter.bind(this);
    }

    async componentDidMount() {
        const { dispatch, match } = this.props;

        dispatch(setLoader(true));

        if (match?.params?.id === "new") {
            const [response, responseTags] = await Promise.all([
                API.get({
                    url: Endpoints.uriBlogPostAuthors(),
                }),
                API.get({
                    url: Endpoints.uriBlogPostTags(),
                }),
            ]);

            if (response.ok) {
                this.setState({
                    authors: response.data.results.authors || [],
                });
            }

            if (responseTags.ok) {
                this.setState({
                    defaultTags: responseTags.data.results || [],
                });
            }

            dispatch(setTitle(`${Strings.blogPosts.single} - ${Strings.blogPosts.new}`));
        } else {
            const [response, responseAuthors, responseTags] = await Promise.all([
                API.get({
                    url: Endpoints.uriBlogPosts(match?.params?.id),
                }),
                API.get({
                    url: Endpoints.uriBlogPostAuthors(),
                }),
                API.get({
                    url: Endpoints.uriBlogPostTags(),
                }),
            ]);

            if (response.ok) {
                this.setState({
                    post: response?.data?.results?.post,
                    ...response?.data?.results?.post,
                    active: response?.data?.results?.post?.active,
                });

                dispatch(setTitle(`${Strings.blogPosts.single} - ${translate(response?.data?.results?.post?.title)}`));
            } else {
                dispatch(replace("/blog-posts"));
                toast.error(Strings.notFound.header);
            }

            if (responseAuthors.ok) {
                this.setState({
                    authors: responseAuthors.data.results.authors || [],
                });
            }

            if (responseTags.ok) {
                this.setState({
                    defaultTags: responseTags.data.results || [],
                });
            }
        }

        delayedDispatch(
            setBreadcrumb(() => {
                const { hasUnsavedFields, language, active, highlight, post } = this.state;

                const actions: Action[] = [
                    {
                        type: "switch",
                        text: Strings.generic.active,
                        value: active,
                        onClick: this.handleSwitch,
                        small: {
                            switch: true,
                            text: true,
                        },
                    },
                    {
                        type: "switch",
                        text: Strings.blogPosts.highlight,
                        value: highlight,
                        onClick: this.handleHighlightSwitch,
                        small: {
                            switch: true,
                            text: true,
                        },
                    },
                    {
                        type: "language",
                        value: language,
                        onChange: this.handleLanguageChange,
                        className: "BreadcrumbLanguage",
                        margin: "left",
                    },
                    {
                        type: "button",
                        text: Strings.generic.save,
                        onClick: this.handleSave,
                        disabled: !hasUnsavedFields,
                        className: hasUnsavedFields ? "BreadcrumbButtonSuccess" : "",
                        separator: "left",
                        isSave: true,
                    },
                ];

                if (match?.params?.id !== "new") {
                    actions[0].separator = "left";

                    actions.unshift({
                        type: "button",
                        text: post.sentToNewsletter
                            ? Strings.blogPosts.newsLetterAlreadySent
                            : Strings.blogPosts.sendNewsletter,
                        disabled: post.sentToNewsletter,
                        onClick: this.handleSendNewsletter,
                        className: "BreadcrumbButtonSuccess",
                    });
                }

                return {
                    locations: [
                        {
                            text: Strings.sidebar.blogPosts,
                            route: "/blog-posts",
                            icon: "testimonial",
                        },
                        {
                            text: translate(post.title),
                            icon: "file",
                            route: `/blog-posts/${match?.params?.id}`,
                        },
                    ],
                    actions: actions,
                };
            })
        );

        dispatch(setLoader(false));
    }

    componentDidUpdate() {
        const { dispatch } = this.props;

        dispatch(updateCrumb());
    }

    isValid() {
        const { title, url, smallDescription, emailDescription, description, date, mainImage, author, tags } =
            this.state;

        if (!translate(title)) return false;
        if (!translate(smallDescription)) return false;
        if (!translate(emailDescription)) return false;
        if (!translate(description)) return false;
        if (!url) return false;
        if (!date) return false;
        if (!mainImage) return false;
        if (!author) return false;
        if (tags.length === 0) return false;

        return true;
    }

    onAuthorChange(value: string) {
        this.setState((state: any) => ({
            author: value,
            hasUnsavedFields: true,
        }));
    }

    async handleSave(event: any) {
        const { match, dispatch } = this.props;
        const {
            active,
            highlight,
            title,
            url,
            author,
            seoDescription,
            keywords,
            smallDescription,
            emailDescription,
            description,
            date,
            mainImage,
            images,
            filesToDelete,
        } = this.state;

        if (!this.isValid()) {
            return toast.warn(Strings.errors.invalidFields);
        }

        if (url?.trim().split(" ")?.length > 1) {
            return toast.warn(Strings.blogPosts.invalidUrl);
        }

        // only allow alpha characters, dashes, and underscores
        const urlRegex = /^[a-zA-Z0-9-_]+$/;
        if (!urlRegex.test(url)) {
            return toast.warn(Strings.blogPosts.invalidUrl);
        }

        dispatch(setLoader(true));

        try {
            // if (smallDescription) {
            //     Object.keys(smallDescription).forEach((key) => {
            //         smallDescription[key] = smallDescription[key].replaceAll("&lt;", "<");
            //         smallDescription[key] = smallDescription[key].replaceAll("&gt;", ">");
            //         smallDescription[key] = smallDescription[key].replaceAll("&amp;", "&");
            //     });
            // }

            // Object.keys(emailDescription).forEach((key) => {
            //     emailDescription[key] = emailDescription[key].replaceAll("&lt;", "<");
            //     emailDescription[key] = emailDescription[key].replaceAll("&gt;", ">");
            //     emailDescription[key] = emailDescription[key].replaceAll("&amp;", "&");
            // });

            // Object.keys(description).forEach((key) => {
            //     description[key] = description[key].replaceAll("&lt;", "<");
            //     description[key] = description[key].replaceAll("&gt;", ">");
            //     description[key] = description[key].replaceAll("&amp;", "&");
            // });

            const body = new FormData();
            body.append("active", active);
            body.append("highlight", highlight);
            body.append("title", JSON.stringify(title));
            body.append("emailDescription", JSON.stringify(emailDescription));
            body.append("description", JSON.stringify(description));
            body.append("author", author);
            body.append("seoDescription", seoDescription);
            body.append("keywords", keywords);
            body.append("date", date);
            body.append("url", url.toLowerCase());
            body.append("tags", JSON.stringify(this.state.tags));
            if (translate(smallDescription)) body.append("smallDescription", JSON.stringify(smallDescription));

            if (images.length > 0) {
                const filteredImages = images.filter((image: any) => typeof image === "string");
                if (filteredImages.length > 0) {
                    body.append("images", JSON.stringify(filteredImages));
                }

                images.forEach((image: any, i: number) => {
                    if (typeof image !== "string") {
                        body.append(`image_${i}`, image.file);
                    }
                });
            }

            body.append("mainImage", mainImage && typeof mainImage === "object" ? mainImage.file : mainImage);
            if (filesToDelete.length) {
                body.append("filesToDelete", JSON.stringify(filesToDelete));
            }

            const request = match?.params?.id === "new" ? API.post : API.put;

            const response = await request({
                url: Endpoints.uriBlogPosts(match?.params?.id === "new" ? "" : match?.params?.id),
                data: body,
            });

            if (response.ok) {
                if (match?.params?.id === "new") {
                    dispatch(replace(`/blog-posts/${response?.data?.results?.post?._id}`));
                    dispatch(updateCrumb());

                    toast.success(Strings.blogPosts.saved);
                } else {
                    this.setState({
                        post: response?.data?.results?.post,
                        ...response?.data?.results?.post,
                        active: response?.data?.results?.post?.active,
                        filesToDelete: [],
                        hasUnsavedFields: false,
                    });

                    toast.success(Strings.blogPosts.updated);
                }
            }
        } catch (error) {
            console.log("error", error);
        }

        dispatch(setLoader(false));
    }

    handleSendNewsletter = async () => {
        const {
            post: { sentToNewsletter },
        } = this.state;
        const { match, dispatch } = this.props;

        if (sentToNewsletter) {
            return toast.warning(Strings.blogPosts.newsLetterAlreadySent);
        }

        dispatch(setLoader(true));

        try {
            const response = await API.post({
                url: Endpoints.uriBlogPosts(`newsletter/${match?.params?.id}`),
            });

            if (response.ok) {
                this.setState((state: any) => ({ post: { ...state.post, sentToNewsletter: true } }));
                toast.success(Strings.blogPosts.newsLetterAlreadySent);
            }
        } catch (error) {
            toast.error(Strings.blogPosts.errorSendingNewsletter);
        } finally {
            dispatch(setLoader(false));
        }
    };

    handleLanguageChange(value: any, options?: any) {
        this.setState({ language: value });
    }

    handleSwitch(value: any) {
        this.setState({ active: value, hasUnsavedFields: true });
    }

    handleHighlightSwitch(value: any) {
        this.setState({ highlight: value, hasUnsavedFields: true });
    }

    getBase64 = (file: any) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    };

    onDropMain = (files: any) => {
        try {
            const file = files[files.length - 1];

            new Compressor(file, {
                mimeType: "image/png",
                success: (result: any) => {
                    this.getBase64(result).then((res) => {
                        this.setState((state: any) => ({
                            mainImage: { file: result, preview: res },
                            filesToDelete: typeof state.mainImage === "string" ? state.mainImage : state.filesToDelete,
                            hasUnsavedFields: true,
                        }));
                    });
                },
            });
        } catch (err) {
            toast.warning(Strings.errors.notSupportedFile);
        }
    };

    onDropImage = (files: any) => {
        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];

                new Compressor(file, {
                    mimeType: "image/png",
                    success: (result: any) => {
                        this.getBase64(result).then((res) => {
                            this.setState((state: any) => ({
                                images: state.images.concat({ file: result, preview: res }),
                                hasUnsavedFields: true,
                            }));
                        });
                    },
                });
            }
        } catch (err) {
            toast.warning(Strings.errors.notSupportedFile);
        }
    };

    handleRemoveImage = (image: any, index: number) => {
        const { filesToDelete } = this.state;
        const markedToDelete = filesToDelete?.includes(image);

        if (markedToDelete) {
            filesToDelete.splice(filesToDelete.indexOf(image), 1);
            this.setState({
                filesToDelete: filesToDelete,
                hasUnsavedFields: true,
            });
        } else {
            this.setState((state: any) => ({
                images:
                    typeof image === "string" ? state.images : state.images.filter((_: any, i: number) => i !== index),
                filesToDelete: typeof image === "string" ? state.filesToDelete.concat(image) : state.filesToDelete,
                hasUnsavedFields: true,
            }));
        }
    };

    renderGeneral() {
        const {
            title,
            mainImage,
            date,
            url,
            seoDescription,
            keywords,
            language,
            smallDescription,
            emailDescription,
            description,
            author,
            authors,
            defaultTags,
            tags,
        } = this.state;

        return (
            <ContentWrapper extraStyle={{ paddingLeft: "20px", paddingRight: "20px" }}>
                <Row gutter={[24, 24]}>
                    <Col xs={24} lg={12}>
                        <label className="SingleLabel __required">{Strings.fields.image}</label>
                        <div className="ImageContainer BlogImage">
                            <div className="Image">
                                <img
                                    alt={Strings.fields.image}
                                    src={(mainImage?.preview ? mainImage.preview : mainImage) || placeholder}
                                />
                                <div className="ImageOverlay">
                                    <Dropzone
                                        accept="image/jpg, image/jpeg, image/png"
                                        className="ImageOverlayOption"
                                        onDrop={(files) => this.onDropMain(files)}
                                    >
                                        <Icon name="pencil-outline" />
                                    </Dropzone>
                                    <div
                                        className={`ImageOverlayOption${Boolean(mainImage) ? "" : " __disabled"}`}
                                        onClick={() => {
                                            if (!Boolean(mainImage)) return;
                                            this.setState((state: any) => ({
                                                mainImage: null,
                                                filesToDelete:
                                                    typeof state.image === "string"
                                                        ? [state.mainImage]
                                                        : state.filesToDelete,
                                                hasUnsavedFields: true,
                                            }));
                                        }}
                                    >
                                        <Icon name="trash" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col xs={24} lg={12}>
                        <Row>
                            <label className="SingleLabel __required">{Strings.blogPosts.title}</label>
                            <Input
                                placeholder={Strings.blogPosts.single}
                                value={title?.[language] || ""}
                                onChange={(e: any) => {
                                    const { value } = e.target;
                                    this.setState((state: any) => ({
                                        title: { ...state.title, [language]: value },
                                        hasUnsavedFields: true,
                                        error: {
                                            ...state.error,
                                            [state.language]: {
                                                ...state.error[state.language],
                                                title: value ? "" : Strings.errors.fillField,
                                            },
                                        },
                                    }));
                                }}
                            />
                        </Row>
                        <Row>
                            <label className="generic-label __required">{Strings.blogPosts.author}</label>
                            <Select
                                style={{ width: "100%" }}
                                placeholder={Strings.blogPosts.author}
                                optionFilterProp="children"
                                onChange={this.onAuthorChange}
                                filterOption={(input: any, option: any) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                                value={author}
                            >
                                {authors.map((author: any) => (
                                    <Select.Option key={author._id} value={author._id}>
                                        {author.name}
                                        {author.position ? ` (${author.position})` : ""}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Row>
                        <Row>
                            <label className="SingleLabel __required">{Strings.fields.date}</label>
                            <DatePicker
                                placeholder={Strings.fields.date}
                                style={{ width: "100%" }}
                                format="DD-MM-YYYY"
                                value={date ? moment(date) : null}
                                onChange={(e) => {
                                    this.setState((state: any) => ({
                                        date: e?.format(),
                                        hasUnsavedFields: true,
                                        error: {
                                            ...state.error,
                                            [state.language]: {
                                                ...state.error[state.language],
                                                date: e?.format() ? "" : Strings.errors.fillField,
                                            },
                                        },
                                    }));
                                }}
                            />
                        </Row>
                        <Row>
                            <label className="SingleLabel __required">{Strings.logs.url}</label>
                            <Input
                                placeholder="URL"
                                value={url || ""}
                                onChange={(e: any) => {
                                    const value = e.target.value;

                                    this.setState((state: any) => ({
                                        url: value,
                                        hasUnsavedFields: true,
                                        error: {
                                            ...state.error,
                                            [state.language]: {
                                                ...state.error[state.language],
                                                url: value ? "" : Strings.errors.fillField,
                                            },
                                        },
                                    }));
                                }}
                            />
                        </Row>
                        <Row>
                            <label className="SingleLabel __required">{Strings.blogPosts.tags}</label>
                            <Select
                                mode="multiple"
                                style={{ width: "100%" }}
                                placeholder={Strings.blogPosts.tags}
                                onChange={(value: any) => {
                                    this.setState({
                                        tags: value,
                                        hasUnsavedFields: true,
                                    });
                                }}
                                value={tags}
                                optionLabelProp="label"
                            >
                                {defaultTags.map((tag: any) => (
                                    <Select.Option key={tag._id} value={tag._id} label={tag.name}>
                                        {tag.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Row>
                    </Col>
                </Row>
                <Row>
                    <label className="SingleLabel">{Strings.fields.seoDescription}</label>
                    <TextArea
                        placeholder={Strings.fields.seoDescription}
                        value={seoDescription || ""}
                        rows={4}
                        onChange={(e: any) => {
                            const { value } = e.target;
                            this.setState({
                                seoDescription: value,
                                hasUnsavedFields: true,
                            });
                        }}
                    />
                </Row>
                <Row>
                    <label className="SingleLabel">{Strings.fields.keywords}</label>
                    <TextArea
                        placeholder={Strings.fields.keywords}
                        value={keywords || ""}
                        rows={4}
                        onChange={(e: any) => {
                            const { value } = e.target;
                            this.setState({
                                keywords: value,
                                hasUnsavedFields: true,
                            });
                        }}
                    />
                </Row>
                <Row gutter={[24, 12]} style={{ marginTop: "20px" }}>
                    <Col xs={24}>
                        <label className="SingleLabel">{Strings.projects.smallDescription}</label>
                        <Editor
                            extraData={language}
                            btnBlacklist={["source"]}
                            value={smallDescription ? smallDescription[language] : ""}
                            onChange={(data: any) => {
                                this.setState({
                                    smallDescription: {
                                        ...smallDescription,
                                        [language]: data,
                                    },
                                    hasUnsavedFields: true,
                                });
                            }}
                        />
                    </Col>
                    <Col xs={24}>
                        <div className="MultiLabel __required">
                            <label>{Strings.projects.emailDescription}</label>
                            <small>{Strings.blogPosts.imageTags}</small>
                        </div>
                        <Editor
                            extraData={language}
                            btnBlacklist={["source"]}
                            value={emailDescription ? emailDescription[language] : ""}
                            onChange={(data: any) => {
                                this.setState({
                                    emailDescription: {
                                        ...emailDescription,
                                        [language]: data,
                                    },
                                    hasUnsavedFields: true,
                                });
                            }}
                        />
                    </Col>
                    <Col xs={24}>
                        <div className="MultiLabel __required">
                            <label>{Strings.projects.description}</label>
                            <small>{Strings.blogPosts.imageTags}</small>
                        </div>
                        <Editor
                            extraData={language}
                            btnBlacklist={["source"]}
                            value={description ? description[language] : ""}
                            onChange={(data: any) => {
                                this.setState({
                                    description: {
                                        ...description,
                                        [language]: data,
                                    },
                                    hasUnsavedFields: true,
                                });
                            }}
                        />
                    </Col>
                </Row>
            </ContentWrapper>
        );
    }

    renderImages() {
        const { images, filesToDelete } = this.state;

        return (
            <ContentWrapper extraStyle={{ paddingLeft: "20px", paddingRight: "20px" }}>
                <Row gutter={[24, 12]} style={{ marginTop: "20px" }}>
                    <Col xs={24} md={6}>
                        <Dropzone
                            accept="image/jpg, image/jpeg, image/png"
                            className="ImageOverlayOption"
                            onDrop={(files) => this.onDropImage(files)}
                        >
                            <Icon name="plus" />
                            <span>{Strings.blogPosts.addImage}</span>
                        </Dropzone>
                    </Col>
                    {images?.map((image: any, i: number) => {
                        const hasPreview = image?.preview && typeof image?.preview === "string";
                        const markedToDelete = filesToDelete?.includes(image);

                        return (
                            <Col xs={24} md={6} key={i}>
                                <div className="ImageGalleryItem">
                                    <div className="Image">
                                        <img alt={Strings.fields.image} src={hasPreview ? image.preview : image} />
                                        <div className="ImageOverlay">
                                            <button onClick={() => this.handleRemoveImage(image, i)}>
                                                <Icon name="trash" />
                                            </button>
                                        </div>
                                        {markedToDelete && (
                                            <div className="ImageOverlayMarked">{Strings.blogPosts.markedToDelete}</div>
                                        )}
                                        {image.preview && (
                                            <div className="ImageOverlayMarked">{Strings.generic.newF}</div>
                                        )}
                                    </div>
                                    <div className="ImageLabel">{`<%images_${i}%>`}</div>
                                </div>
                            </Col>
                        );
                    })}
                </Row>
            </ContentWrapper>
        );
    }

    render() {
        const { selectedTab, title } = this.state;
        const { match } = this.props;

        const tabs = [
            {
                key: "general",
                title: Strings.projects.general,
                onClick: () => this.setState({ selectedTab: "general" }),
                renderScreen: () => this.renderGeneral(),
            },
            {
                key: "images",
                title: Strings.fields.images,
                onClick: () => this.setState({ selectedTab: "images" }),
                renderScreen: () => this.renderImages(),
            },
        ];

        return (
            <React.Fragment>
                <Helmet>
                    <title>
                        {match?.params?.id === "new"
                            ? Strings.blogPosts.new
                            : translate(title) || Strings.blogPosts.single}
                    </title>
                    <meta name="description" content="Description of Blog Post Detail" />
                </Helmet>
                <SelectableTabs selectedTab={selectedTab} tabs={tabs}></SelectableTabs>
            </React.Fragment>
        );
    }
}

const mapStateToProps = (state: any) => ({});

export default connect(mapStateToProps)(BlogPostDetail);
